import { Button, ButtonProps } from '@givebutter/primitives/button';
import { useMediaQuery } from '@givebutter/react-util';
import { faTriangleExclamation } from '@fortawesome/pro-regular-svg-icons';
import { faClose } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import * as Dialog from 'components/dialog';
import * as Tooltip from 'components/tooltip';
import { twMerge } from 'tailwind-merge';

interface FloatingChecklistDismissButtonProps extends Omit<ButtonProps, 'variant' | 'children'> {}
const FloatingChecklistDismissButton = ({
    className,
    ...rest
}: FloatingChecklistDismissButtonProps) => {
    return (
        <Tooltip.Root>
            <Tooltip.Trigger asChild>
                <Button
                    {...rest}
                    aria-label="Hide open-checklist button temporarily"
                    variant="none"
                    className={twMerge(
                        'absolute -left-3 -top-6 size-6 rounded-full bg-gray-100 hover:bg-gray-300 sm:-top-4 sm:left-1 sm:opacity-0 sm:hover:opacity-100 sm:focus:opacity-100',
                        className,
                    )}
                >
                    <FontAwesomeIcon icon={faClose} size="xs" className="text-gray-500" />
                </Button>
            </Tooltip.Trigger>
            <Tooltip.Content inverse sideOffset={12}>
                <p className="font-medium text-white">Hide temporarily</p>
            </Tooltip.Content>
        </Tooltip.Root>
    );
};

interface DismissPromptDialogProps extends Dialog.DialogProps {}
const DismissPromptDialog = ({ children, ...rest }: DismissPromptDialogProps) => {
    const isMobile = useMediaQuery(`(max-width: 640px)`);
    return (
        <Dialog.Root {...rest}>
            <Dialog.Portal>
                <Dialog.Overlay />
                <Dialog.Content className="max-w-[400px] p-4 sm:p-6">
                    <Dialog.Header className="gap-4">
                        <div className="inline-flex size-10 flex-shrink-0 items-center justify-center rounded-full bg-orange-100 text-orange-600">
                            <FontAwesomeIcon
                                icon={faTriangleExclamation}
                                size="lg"
                                className="-mt-0.5"
                            />
                        </div>
                    </Dialog.Header>
                    <Dialog.Body className="gap-8">
                        <div>
                            <Dialog.Title>Hide checklist forever?</Dialog.Title>
                            <p className="text-sm text-gray-600">
                                {isMobile
                                    ? 'We will permanently remove the checklist. You can access it on the home page, or hide the checklist just for this session.'
                                    : 'We’ve noticed you have hidden this checklist multiple times. Would you like to hide this forever?'}
                            </p>
                        </div>
                        <div>{children}</div>
                    </Dialog.Body>
                    <Dialog.Close />
                </Dialog.Content>
            </Dialog.Portal>
        </Dialog.Root>
    );
};

export { FloatingChecklistDismissButton, DismissPromptDialog };
